import React, { useState } from 'react';
import { Card, Typography, Button, message, Tag, Spin } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { configService } from '../../services';
import { getApiErrorMessage } from '../../utils/error';
import './APIConfigPage.css';

const { Title, Text } = Typography;

const APIConfigPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['inbound-api-config'],
    queryFn: () => configService.getInboundApiConfig(),
  });

  const config = apiResponse?.data?.data;

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => {
      message.success(`Đã sao chép ${label}!`);
    });
  };

  const handleDownloadGuide = async () => {
    try {
      setDownloading(true);
      const blob = await configService.downloadManualConnectPartner();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tai-lieu-huong-dan-ket-noi';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Tải file thành công!');
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Không thể tải file hướng dẫn!'));
    } finally {
      setDownloading(false);
    }
  };

  const maskedPassword = config?.password
    ? config.password.replace(/./g, '*')
    : '••••••••••••••';

  return (
    <div className="api-config-page">
      <Card
        title={
          <Title level={5} style={{ margin: 0 }}>
            Cấu hình API toa thuốc
          </Title>
        }
        className="api-info-card"
      >
        {isLoading ? (
          <div className="api-loading">
            <Spin />
          </div>
        ) : (
          <div className="api-info-list">
            <div className="api-info-row">
              <Text className="api-info-label">API Server</Text>
              <div className="api-info-value">
                <Text>{config?.allowed_hosts || '—'}</Text>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  size="small"
                  className="copy-btn"
                  onClick={() => handleCopy(config?.allowed_hosts || '', 'API Server')}
                >
                  Sao chép
                </Button>
              </div>
            </div>

            <div className="api-info-row">
              <Text className="api-info-label">Tài khoản</Text>
              <div className="api-info-value">
                <Text>{config?.username || '—'}</Text>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  size="small"
                  className="copy-btn"
                  onClick={() => handleCopy(config?.username || '', 'Tài khoản')}
                >
                  Sao chép
                </Button>
              </div>
            </div>

            <div className="api-info-row">
              <Text className="api-info-label">Mật khẩu</Text>
              <div className="api-info-value">
                <Text
                  style={{ cursor: 'pointer', letterSpacing: showPassword ? 'normal' : '2px' }}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? config?.password : maskedPassword}
                </Text>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  size="small"
                  className="copy-btn"
                  onClick={() => handleCopy(config?.password || '', 'Mật khẩu')}
                >
                  Sao chép
                </Button>
              </div>
            </div>

            {config?.status && (
              <div className="api-info-row">
                <Text className="api-info-label">Trạng thái</Text>
                <div className="api-info-value">
                  <Tag color={config.status === 'active' ? 'green' : 'red'}>
                    {config.status === 'active' ? 'Đang hoạt động' : 'Không hoạt động'}
                  </Tag>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="guide-section" style={{ marginLeft: 24 }}>
        <Text>File hướng dẫn kết nối ở </Text>
        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={handleDownloadGuide}
          loading={downloading}
          className="guide-download-link"
        >
          link này
        </Button>
      </div>

      {config?.notes && (
        <div className="guide-section">
          <Title level={5}>Hướng dẫn kết nối</Title>
          <pre className="guide-code-block">{config.notes}</pre>
        </div>
      )}
    </div>
  );
};

export default APIConfigPage;
